import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UsuarioContext } from "../../../../../providers/userContext";
import { Sparkles, SendHorizontal, PenSquare, Building2, Boxes, Wrench, MessageCircleMore, ThumbsUp, ThumbsDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { createChat, sendMessage, listChats, getChatRecord, sendFeedback } from "../../../../../services/lisIA.service";
import { getListFincas } from "../../../../../services";

function LegalIA({ modelo = "normativo" }) {
  const { t } = useTranslation('common');
  const user = useContext(UsuarioContext);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [input, setInput] = useState("");
  const [loadingConvs, setLoadingConvs] = useState({});
  const [showMobileHistory, setShowMobileHistory] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [loadingPhraseIdx, setLoadingPhraseIdx] = useState(0);
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const [comunidadNames, setComunidadNames] = useState([]);
  const loadingPhrases = t('legalIA.loadingPhrases', { returnObjects: true });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const streamTimers = useRef(new Set());
  const activeConv = conversations.find(c => c.id === activeConvId);
  const messages = activeConv?.messages || [];
  const isActiveLoading = activeConvId ? !!loadingConvs[activeConvId] : false;
  const nombre = (user.nombreContacto || user.nombre)?.split(" ")[0] || "usuario";

  useEffect(() => {
    // Ocultar scroll de la página mientras el chat está montado
    const contenedor = document.querySelector('.contenedor');
    if (contenedor) {
      contenedor.style.overflow = 'hidden';
      contenedor.style.paddingBottom = '0';
      contenedor.style.minHeight = 'unset';
      contenedor.style.height = 'calc(100vh - 56px)';
    }
    return () => {
      if (contenedor) {
        contenedor.style.overflow = '';
        contenedor.style.paddingBottom = '';
        contenedor.style.minHeight = '';
        contenedor.style.height = '';
      }
    };
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isActiveLoading]);

  // Limpia las animaciones de escritura pendientes al desmontar.
  useEffect(() => {
    const timers = streamTimers.current;
    return () => {
      timers.forEach(id => clearInterval(id));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    if (!isActiveLoading || !Array.isArray(loadingPhrases) || loadingPhrases.length === 0) return;
    setLoadingPhraseIdx(Math.floor(Math.random() * loadingPhrases.length));
    const interval = setInterval(() => {
      setLoadingPhraseIdx(prev => (prev + 1) % loadingPhrases.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isActiveLoading, loadingPhrases.length]);

  const isNormativo = modelo === "normativo";
  const userEmail = user.email || "";

  const suggestedQuestions = t('legalIA.suggestedQuestions', {
    returnObjects: true,
    defaultValue: [
      '¿Me puedes hacer un resumen cuantificado por riesgo y comunidad de propietarios?',
      '¿Cuantas comunidades tienen riesgos de contactos eléctricos?',
      '¿Cuáles son los riesgos de la primera comunidad?',
      '¿Me puedes hacer un resumen de los comentarios de la hoja de visita de la primera comunidad?',
      '¿La primera comunidad dispone de número suficiente de extintores?',
      '¿Qué comunidades tengo?',
      '¿Cuál es el riesgo más frecuente entre todas mis comunidades?',
    ],
  });

  // Cargar nombres de comunidades del AAFF (para sustituir "la primera comunidad")
  useEffect(() => {
    if (!isNormativo) return;
    getListFincas({ filtros: {}, params: { page: 1, limit: 1000 } })
      .then(res => {
        const names = (res?.data || [])
          .map(c => c.razonSocial)
          .filter(Boolean);
        setComunidadNames(names);
      })
      .catch(err => console.error("Error loading communities:", err));
  }, [isNormativo]);

  // 3 preguntas aleatorias (se rebarajan al montar y al iniciar nueva consulta).
  // Las que mencionan "la primera comunidad" usan una comunidad aleatoria del AAFF.
  const displayedQuestions = useMemo(() => {
    const arr = Array.isArray(suggestedQuestions) ? [...suggestedQuestions] : [];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    const picked = arr.slice(0, 3);
    const comunidad = comunidadNames.length > 0
      ? comunidadNames[Math.floor(Math.random() * comunidadNames.length)]
      : null;
    return picked.map(q =>
      comunidad ? q.replace(/primera comunidad/gi, `comunidad ${comunidad}`) : q
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shuffleSeed, comunidadNames]);

  const handleSuggestionClick = (question) => {
    if (isActiveLoading) return;
    handleSend(question);
  };

  const handleRate = (messageIdx, rating) => {
    if (!activeConvId) return;
    const conv = conversations.find(c => c.id === activeConvId);
    const msg = conv?.messages?.[messageIdx];
    const nextRating = msg?.rating === rating ? null : rating;

    setConversations(prev =>
      prev.map(c => c.id === activeConvId
        ? {
            ...c,
            messages: c.messages.map((m, i) =>
              i === messageIdx
                ? { ...m, rating: nextRating }
                : m
            ),
          }
        : c
      )
    );

    if (msg?.interactionId && nextRating) {
      sendFeedback(msg.interactionId, nextRating === 'up' ? 'like' : 'dislike')
        .catch(err => console.error('Error sending feedback:', err));
    }
  };

  // Cargar historial al montar (normativo y jurídico)
  useEffect(() => {
    listChats(userEmail, modelo)
      .then(data => {
        const chats = (Array.isArray(data) ? data : data?.chats || [])
          .map(c => ({ id: c.id_chat || c.chat_id || c.id, title: c.title || c.name || "Consulta", messages: [] }));
        setConversations(chats);
      })
      .catch(err => console.error("Error loading chat history:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelo]);

  const handleNewConversation = () => {
    setActiveConvId(null);
    setInput("");
    setShuffleSeed(s => s + 1);
  };

  // Cargar mensajes al seleccionar conversación
  const handleSelectConversation = async (convId) => {
    setActiveConvId(convId);
    const conv = conversations.find(c => c.id === convId);
    if (conv && conv.messages.length > 0) return;
    try {
      const data = await getChatRecord(convId, userEmail);
      const records = Array.isArray(data) ? data : data?.messages || data?.records || [];
      const msgs = records.flatMap(r => {
        const pair = [];
        const interactionId = r.interaction_id || r.id_interaction || r.interactionId || r.id || null;
        const fb = r.feedback;
        const rating = fb === 'like' ? 'up' : fb === 'dislike' ? 'down' : null;
        if (r.question) pair.push({ role: "user", content: r.question });
        if (r.answer) pair.push({ role: "assistant", content: r.answer, interactionId, rating });
        return pair;
      });
      setConversations(prev =>
        prev.map(c => c.id === convId ? { ...c, messages: msgs } : c)
      );
    } catch (err) {
      console.error("Error loading chat record:", err);
    }
  };

  const markLoading = (convId, value) => {
    setLoadingConvs(prev => {
      const next = { ...prev };
      if (value) next[convId] = true;
      else delete next[convId];
      return next;
    });
  };

  // Efecto de escritura simulado: la API LIS no soporta streaming real,
  // así que la respuesta (ya completa) se revela progresivamente en la UI.
  const animateAssistantMessage = (convId, streamId, fullText) => {
    const len = fullText.length;
    const tick = 24;
    const duration = Math.min(3500, Math.max(700, len * 14));
    const chunk = Math.max(1, Math.ceil(len / Math.max(1, duration / tick)));
    let shown = 0;
    const intervalId = setInterval(() => {
      shown = Math.min(len, shown + chunk);
      const partial = fullText.slice(0, shown);
      const done = shown >= len;
      setConversations(prev =>
        prev.map(c => c.id === convId
          ? {
              ...c,
              messages: c.messages.map(m =>
                m.streamId === streamId
                  ? { ...m, content: partial, streaming: !done }
                  : m
              ),
            }
          : c
        )
      );
      if (done) {
        clearInterval(intervalId);
        streamTimers.current.delete(intervalId);
      }
    }, tick);
    streamTimers.current.add(intervalId);
  };

  const handleSend = async (questionOverride) => {
    const rawQuestion = typeof questionOverride === "string" ? questionOverride : input;
    if (!rawQuestion.trim()) return;
    // Sólo bloquea el reenvío en la MISMA conversación, no en las demás
    if (activeConvId && loadingConvs[activeConvId]) return;
    const question = rawQuestion.trim();
    const userMessage = { role: "user", content: question };
    setInput("");

    // API real (normativo y jurídico)
    // Los ids "temp-..." son optimistas (cliente); la API espera un UUID real,
    // así que se tratan como "sin chat" para forzar la creación con createChat.
    const isTempId = (id) => !id || String(id).startsWith("temp-");

    let chatId = isTempId(activeConvId) ? null : activeConvId;
    let tempId = null;
    if (!chatId) {
      // Reutiliza la conversación optimista activa si ya existe; si no, crea una nueva.
      tempId = (isTempId(activeConvId) && activeConvId) ? activeConvId : `temp-${Date.now()}`;
      const title = question.length > 40 ? question.substring(0, 40) + "..." : question;
      setConversations(prev =>
        prev.some(c => c.id === tempId)
          ? prev.map(c => c.id === tempId ? { ...c, messages: [...c.messages, userMessage] } : c)
          : [{ id: tempId, title, messages: [userMessage] }, ...prev]
      );
      setActiveConvId(tempId);
      markLoading(tempId, true);
    } else {
      setConversations(prev =>
        prev.map(c => c.id === chatId ? { ...c, messages: [...c.messages, userMessage] } : c)
      );
      markLoading(chatId, true);
    }

    try {
      if (!chatId) {
        const chatData = await createChat(userEmail, modelo);
        chatId = chatData?.id_chat || chatData?.chat_id || chatData?.id || null;
        if (isTempId(chatId)) {
          throw new Error("createChat no devolvió un id de chat válido");
        }
        // Reconciliar id temporal → id real
        setConversations(prev => prev.map(c => c.id === tempId ? { ...c, id: chatId } : c));
        setActiveConvId(prev => prev === tempId ? chatId : prev);
        setLoadingConvs(prev => {
          const next = { ...prev };
          delete next[tempId];
          next[chatId] = true;
          return next;
        });
      }

      const response = await sendMessage(chatId, question, userEmail, modelo);
      const aiContent = response.response || response.answer || response.message || response.content || JSON.stringify(response);
      const interactionId = response.interaction_id || response.id_interaction || response.interactionId || response.id || null;
      const streamId = `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // Mensaje vacío que se irá rellenando con el efecto de escritura
      const aiMessage = { role: "assistant", content: "", interactionId, streamId, streaming: true };

      // Actualizar título si la API devuelve uno y añadir el mensaje vacío
      setConversations(prev =>
        prev.map(c => c.id === chatId
          ? { ...c, title: response.title || c.title, messages: [...c.messages, aiMessage] }
          : c
        )
      );

      // Revelar la respuesta de forma progresiva
      animateAssistantMessage(chatId, streamId, aiContent);
    } catch (err) {
      console.error("Error sending message:", err);
      const errorMsg = { role: "assistant", content: t('legalIA.errorResponse', { defaultValue: 'Ha ocurrido un error al procesar tu consulta. Inténtalo de nuevo.' }) };
      const targetId = chatId || tempId;
      setConversations(prev =>
        prev.map(c => c.id === targetId ? { ...c, messages: [...c.messages, errorMsg] } : c)
      );
    } finally {
      setLoadingConvs(prev => {
        const next = { ...prev };
        if (chatId) delete next[chatId];
        if (tempId) delete next[tempId];
        return next;
      });
    }
  };

  const modelLabel = modelo === "normativo" ? t('legalIA.modelNormative') : t('legalIA.modelLegal');

  return (
    <div className="flex flex-col h-full bg-white font-sans">
      <div className="px-4 sm:px-6" style={{ borderBottom: '1px solid #e5e7eb' }}>
        {/* <BreadCrumb rutas={[{ path: '/legal-ia', label: t('nav:sidebar.legalIA', 'Tu Legal IA') }]} /> */}
      </div>
      <div className="flex flex-1 min-h-0">
      {/* Panel izquierdo — Historial*/}
      <div className="hidden md:flex flex-col w-[300px] shrink-0" style={{ borderRight: "1px solid #e5e7eb" }}>
        <div className="p-4 pb-2">
          <h2 className="text-[20px] font-bold text-gray-900 text-center">{t('legalIA.history')}</h2>
        </div>
        <div style={{ borderBottom: '1px solid #e5e7eb' }} />
        <div className="flex-1 overflow-y-auto px-3 pt-2">
          {conversations.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm bg-gray-100 text-gray-900 font-medium cursor-default">
              {t('legalIA.newQuery')}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {conversations.map(c => (
                <button
                  key={c.id}
                  onClick={() => handleSelectConversation(c.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm truncate transition-colors cursor-pointer border-none ${
                    c.id === activeConvId
                      ? "bg-gray-100 text-gray-900 font-medium"
                      : "text-gray-700 hover:bg-gray-50 bg-transparent"
                  }`}
                >
                  {c.title}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-auto p-3 pb-3">
          <button
            onClick={handleNewConversation}
            className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm text-gray-700 bg-transparent cursor-pointer transition-all duration-150 hover:bg-gray-200 hover:text-gray-900 hover:shadow-sm"
            style={{ border: "1px solid #e5e7eb" }}
          >
            <PenSquare size={16} />
            {t('legalIA.newQuery')}
          </button>
        </div>
      </div>

      {/* Panel historial móvil*/}
      {showMobileHistory && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setShowMobileHistory(false)} />
          <div className="md:hidden fixed top-0 left-0 h-full w-[280px] bg-white z-50 flex flex-col shadow-xl">
            <div className="p-4 pb-2 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">{t('legalIA.history')}</h2>
              <button onClick={() => setShowMobileHistory(false)} className="text-gray-400 bg-transparent border-none cursor-pointer p-1">✕</button>
            </div>
            <div style={{ borderBottom: '1px solid #e5e7eb' }} />
            <div className="flex-1 overflow-y-auto px-3 pt-2">
              {conversations.length === 0 ? (
                <div className="px-3 py-2.5 rounded-lg text-sm bg-gray-100 text-gray-900 font-medium">
                  {t('legalIA.newQuery')}
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  {conversations.map(c => (
                    <button
                      key={c.id}
                      onClick={() => { handleSelectConversation(c.id); setShowMobileHistory(false); }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm truncate transition-colors cursor-pointer border-none ${
                        c.id === activeConvId
                          ? "bg-gray-100 text-gray-900 font-medium"
                          : "text-gray-700 hover:bg-gray-50 bg-transparent"
                      }`}
                    >
                      {c.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3">
              <button
                onClick={() => { handleNewConversation(); setShowMobileHistory(false); }}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm text-gray-700 bg-transparent cursor-pointer transition-colors hover:bg-gray-200"
                style={{ border: "1px solid #e5e7eb" }}
              >
                <PenSquare size={16} />
                {t('legalIA.newQuery')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Panel derecho — Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header móvil */}
        <div className="md:hidden flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setShowMobileHistory(true)}
            className="flex items-center gap-2 text-sm text-gray-600 bg-transparent border-none cursor-pointer p-1.5"
          >
            <MessageCircleMore size={20} />
            {t('legalIA.history')}
          </button>
          <button
            onClick={handleNewConversation}
            className="flex items-center gap-1 text-sm text-gray-600 bg-transparent border-none cursor-pointer p-1"
          >
            <PenSquare size={16} />
          </button>
        </div>
        {/* Área de mensajes */}
        <div className={`flex-1 px-4 sm:px-8 ${messages.length > 0 ? "overflow-y-auto" : "overflow-hidden"}`}>
          {messages.length === 0 && !isActiveLoading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="flex items-center -space-x-3 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm" style={{ border: '2px solid #93bbf0', transform: 'rotate(-10deg)' }}>
                  <Building2 size={22} className="text-blue-500" />
                </div>
                <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center shadow-sm z-10" style={{ border: '2px solid #f2bf97', transform: 'translateY(-4px)' }}>
                  <Wrench size={22} className="text-orange-500" />
                </div>
                <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center shadow-sm" style={{ border: '2px solid #c4a5e0', transform: 'rotate(10deg)' }}>
                  <Boxes size={22} className="text-purple-500" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">{t('legalIA.greeting', { name: nombre })}</h2>
              <p className="text-sm text-gray-700">{t('legalIA.helpQuestion')}</p>

              {isNormativo && displayedQuestions.length > 0 && (
                <div className="mt-8 w-full max-w-2xl px-4">
                  <p className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-3 text-center">
                    {t('legalIA.suggestedQuestionsTitle', { defaultValue: 'Preguntas frecuentes' })}
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {displayedQuestions.map((q, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleSuggestionClick(q)}
                        className="text-left text-sm text-gray-700 bg-white border border-gray-200 rounded-full px-4 py-2 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto py-6 space-y-6">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start gap-3"}`}>
                  {msg.role === "assistant" && (
                    <div className="shrink-0 mt-1">
                      <Sparkles size={18} className="text-gray-400" />
                    </div>
                  )}
                  <div className={msg.role === "user" ? "max-w-[80%]" : "min-w-0 max-w-[80%]"}>
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-800 markdown-body"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <>
                        <ReactMarkdown
                          components={{
                            p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                            ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2" {...props} />,
                            ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2" {...props} />,
                            li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                            strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                            a: ({ node, ...props }) => <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" {...props} />,
                            code: ({ node, inline, ...props }) =>
                              inline
                                ? <code className="bg-gray-100 px-1 rounded text-xs" {...props} />
                                : <code className="block bg-gray-100 p-2 rounded text-xs overflow-x-auto" {...props} />,
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                        {msg.streaming && (
                          <span className="inline-block w-[3px] h-[15px] align-middle bg-gray-400 animate-pulse ml-0.5" />
                        )}
                        </>
                      ) : (
                        msg.content.split("\n").map((line, j) => (
                          <p key={j} className="mb-1 last:mb-0">{line}</p>
                        ))
                      )}
                    </div>
                    {msg.role === "assistant" && (
                      <div className="flex items-center gap-1 -mt-4 px-3">
                        <button
                          type="button"
                          onClick={() => handleRate(i, 'up')}
                          aria-label={t('legalIA.markUseful', { defaultValue: 'Útil' })}
                          title={t('legalIA.markUseful', { defaultValue: 'Útil' })}
                          className={`p-1.5 rounded-md border-none bg-transparent transition-colors cursor-pointer ${
                            msg.rating === 'up'
                              ? 'text-green-600 bg-green-50'
                              : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <ThumbsUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRate(i, 'down')}
                          aria-label={t('legalIA.markNotUseful', { defaultValue: 'No útil' })}
                          title={t('legalIA.markNotUseful', { defaultValue: 'No útil' })}
                          className={`p-1.5 rounded-md border-none bg-transparent transition-colors cursor-pointer ${
                            msg.rating === 'down'
                              ? 'text-red-600 bg-red-50'
                              : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <ThumbsDown size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isActiveLoading && (
                <div className="flex items-center gap-2">
                  <Sparkles size={18} className="text-gray-400 animate-pulse" />
                  <span className="text-sm text-gray-700 animate-pulse">
                    {Array.isArray(loadingPhrases) ? loadingPhrases[loadingPhraseIdx] : ''}
                  </span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-4 sm:px-8 pb-8 sm:pb-3 pt-2">
          <div
            className="max-w-3xl mx-auto rounded-xl bg-white transition-colors duration-150"
            style={{ border: inputFocused ? "2px solid #1a1a1a" : "1px solid #888" }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={t('legalIA.placeholder')}
              rows={1}
              className="w-full resize-none border-none outline-none px-4 py-3 text-sm text-gray-900 bg-transparent placeholder:text-gray-700"
              style={{ minHeight: "44px", maxHeight: "150px" }}
            />
            <div className="flex items-center justify-end gap-1 px-2 pb-2">
              <span className="px-3 py-2 rounded-lg text-xs bg-gray-100 text-gray-600">
                {modelLabel}
              </span>
              <button
                onClick={handleSend}
                disabled={!input.trim() || isActiveLoading}
                className={`flex items-center justify-center w-8 h-8 rounded-lg border-none transition-colors ${
                  input.trim() && !isActiveLoading
                    ? "bg-gray-800 text-white cursor-pointer hover:bg-gray-700"
                    : "bg-transparent text-gray-300 cursor-default"
                }`}
              >
                <SendHorizontal size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default LegalIA;
